import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie } from "@phosphor-icons/react";
import { useT } from "../context/LangContext";
import { CONSENT_EVENT, getConsent, setConsent } from "../lib/consent";

export const CookieBanner = () => {
  const { t } = useT();
  const c = t.cookie;
  const [visible, setVisible] = useState(() => !getConsent());

  useEffect(() => {
    const sync = () => setVisible(!getConsent());
    window.addEventListener(CONSENT_EVENT, sync);
    return () => window.removeEventListener(CONSENT_EVENT, sync);
  }, []);

  const choose = (value) => {
    setConsent(value);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="dialog"
          aria-live="polite"
          aria-label={c.title}
          data-testid="cookie-banner"
          className="fixed inset-x-4 bottom-4 z-[90] md:inset-x-auto md:left-8 md:bottom-8 md:max-w-md"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="rounded-2xl border border-border bg-bone/95 p-6 shadow-2xl backdrop-blur-md">
            <div className="flex items-start gap-4">
              <div className="rounded-xl bg-ochre/15 p-2.5 shrink-0">
                <Cookie size={22} weight="duotone" className="text-ochre" />
              </div>
              <div>
                <h3 className="font-display font-bold tracking-tight text-base text-ink">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-stone">
                  {c.text}{" "}
                  <Link to="/privacy" data-testid="cookie-more-link" className="font-semibold text-terracotta underline-offset-2 hover:underline">
                    {c.more}
                  </Link>
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => choose("necessary")}
                data-testid="cookie-necessary-btn"
                className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-semibold text-ink transition-colors duration-200 hover:bg-ink hover:text-bone"
              >
                {c.necessary}
              </button>
              <button
                type="button"
                onClick={() => choose("all")}
                data-testid="cookie-accept-btn"
                className="rounded-full bg-terracotta px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-terracotta-dark"
              >
                {c.accept}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
